import {
  Heading,
  Box,
  Button,
  Text,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  Flex,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { useState } from 'react';
import { useConfirm } from '@src/hooks/useConfirm';
import { useDeleteEntry, useGetAdminContent } from '@src/modules/settings/context/settings-api-hooks';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import BasicEntryEdit from './BasicEntryEdit';

export default function BasicEntryList({ typeKey, typeLabel, typeId }) {
  const { data: entries, isLoading } = useGetAdminContent(typeKey);
  const { mutate: doDeleteEntry, isLoading: isDeleting } = useDeleteEntry(typeKey);
  const [editingEntry, setEditingEntry] = useState(null);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { confirm, ConfirmDialog } = useConfirm();
  const hasData = entries?.length > 0;
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    onOpen();
  };

  const handleDelete = async (id) => {
    setDeletingEntryId(id);
    const confirmed = await confirm({
      title: 'Are you sure you want to delete this entry?',
      body: 'This action cannot be undone.',
      confirmButtonLabel: 'Delete Entry',
    });

    if (confirmed) {
      await doDeleteEntry(id);
      setDeletingEntryId(null);
    }
  };

  const handleClose = () => {
    setEditingEntry(null);
    onClose();
  };

  return (
    <>
      <BasicEntryEdit
        isOpen={isOpen}
        onClose={handleClose}
        selectedEntry={editingEntry}
        typeKey={typeKey}
        typeLabel={typeLabel}
        typeId={typeId}
      />
      <ConfirmDialog confirmButtonColor="red" />
      <HStack justifyContent="space-between" mb={4}>
        <Heading as="h2" size="lg">{typeLabel}</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="orange"
          onClick={onOpen}
        >
          Add New
        </Button>
      </HStack>

      <Flex
        borderRadius="md"
        bg="white"
        p={4}
        boxShadow="sm"
        flexGrow="1"
      >
        {isLoading && <LoadingSpinner />}
        {!hasData && !isLoading && (
          <Flex
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="lg" color="gray.500">
              No
              {' '}
              {typeLabel}
              {' '}
              found.
            </Text>
          </Flex>
        )}
        {hasData && !isLoading && (
          <TableContainer width="100%">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Content</Th>
                  <Th>&nbsp;</Th>
                </Tr>
              </Thead>
              <Tbody>
                {entries.map((item) => (
                  <Tr key={item.id}>
                    <Td>
                      <Text fontSize="sm" color="gray.700">
                        {new Date(item.releaseDate).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </Text>
                    </Td>
                    <Td maxW="400px">
                      <Box
                        as="span"
                        dangerouslySetInnerHTML={{ __html: item.message }}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2} justifyContent="flex-end">
                        <Button size="xs" colorScheme="red" onClick={() => handleDelete(item.id)} variant="ghost" isLoading={deletingEntryId === item.id && isDeleting}>Delete</Button>
                        <Button size="xs" colorScheme="blue" onClick={() => handleEdit(item)} variant="outline">Edit</Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Flex>
    </>
  );
}
